import { useState, useRef, MouseEvent } from 'react';

interface GlowButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  className?: string;
}

export default function GlowButton({ 
  children, 
  onClick, 
  href, 
  target, 
  rel,
  variant = 'primary',
  className = '' 
}: GlowButtonProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const handleMouseMove = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const baseClasses = "relative px-8 py-4 font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer";
  
  const variantClasses = {
    primary: "bg-gradient-to-r from-purple-600 to-purple-500 text-white hover:shadow-lg hover:shadow-purple-500/50",
    secondary: "bg-gradient-to-r from-purple-600 to-green-600 text-white hover:shadow-2xl hover:shadow-purple-500/50",
    outline: "bg-transparent border-2 border-green-400 text-green-400 hover:bg-green-400 hover:text-gray-950"
  };

  const content = (
    <>
      <span className="relative z-10 flex items-center gap-2">
        {children}
      </span>
      <div 
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: `radial-gradient(circle 120px at ${mousePosition.x}px ${mousePosition.y}px, rgba(255, 255, 255, 0.3), transparent)`,
        }}
      />
    </>
  );

  if (href) {
    return (
      <a
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        target={target}
        rel={rel}
        onMouseMove={handleMouseMove}
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
    >
      {content}
    </button>
  );
}
